import { useId, useRef } from "react";
import { tableHeadRowCls, tableHeadCellCls, tableRowHoverCls } from "../styles/ui";
import SortableColumnHeader from "./SortableColumnHeader";
import TableScrollbar from "./TableScrollbar";
import RowLink from "./RowLink";

// Generic list table. columns: [{ key, label, render(row), cellClass? }].
// The columns array is the full ordered set to display (fixed + selected optional).
//
// `sort`/`onSortChange` (optional) add click-to-sort column headers — every
// column registry already carries a `value(row)` for CSV export, and sorting
// reuses it (see utils/sortRows.js), so any column is sortable for free.
//
// `columnWidths` (optional, from hooks/useColumnWidths.js) adds drag-to-resize
// column headers. table-layout:fixed + a <colgroup> sets the baseline, but
// an explicit `max-width` on each <td>/<th> itself (see below) is what
// actually makes a column able to go NARROWER than its content — confirmed
// by direct browser testing that table-layout:fixed alone still lets a
// column's width float up to its longest content despite the <col> saying
// otherwise. The LAST column deliberately gets no explicit width/max-width
// (a bare <col/>), so it absorbs whatever space the others leave, which is
// also what makes hiding/showing an optional column need no separate
// redistribution logic (the "last" key is just recomputed from the live
// `columns` array every render).
export function DataTable({ columns, rows, isLoading, emptyText = "No results", onRowClick, rowHref, rowKey = (r) => r.id, sort, onSortChange, columnWidths }) {
  const scrollRef = useRef(null);
  const scrollId = useId();

  if (isLoading) {
    return <div className="flex items-center justify-center h-40 text-ink-muted text-sm">Loading…</div>;
  }
  if (!rows.length) {
    return <div className="flex items-center justify-center h-40 text-ink-muted text-sm">{emptyText}</div>;
  }

  const lastKey = columns[columns.length - 1]?.key;

  return (
    // flex-1 min-h-0 so this fills whatever bounded-height flex ancestor its
    // page gives it (the Jobs/Visits/PurchaseOrders pages' own scrolling
    // card, `flex-1 overflow-auto ${cardCls}` → now `flex-1 min-h-0 flex
    // flex-col overflow-hidden`) — required for the sticky header below to
    // work at all. See the long comment on the scroll div for why.
    <div className="flex-1 min-h-0 flex flex-col">
      <TableScrollbar scrollRef={scrollRef} scrollTargetId={scrollId} />
      {/* ⚠️⚠️⚠️ ONE div doing BOTH horizontal AND vertical scroll — not two
          split across an inner overflow-x-auto and an outer overflow-y-auto
          card, which is what this used to be and is why the sticky header
          below silently stopped working. Confirmed empirically (Playwright,
          not guessed) that `position: sticky` can NEVER escape past an
          ancestor with `overflow-x: auto` to reach a farther-out real
          scroller, REGARDLESS of what overflow-y is set to (visible, auto,
          hidden, clip — all fail identically) — any non-`visible` axis
          value marks the box as sticky's containing reference frame, and if
          that box never actually scrolls (its height just grows to fit
          content, which is what an inner wrapper with no bounded height
          does), the header is "stuck" relative to something that never
          moves, i.e. it behaves like `position: static`. The fix is to make
          THIS div both the thing that scrolls AND the thing sticky attaches
          to, which only works because it's ALSO given a real bounded height
          (flex-1 min-h-0, inherited from the page's own flex-column shell)
          — a genuinely non-bounded div (like a Dashboard mini-table with no
          such ancestor) degrades safely back to today's non-sticky
          behavior: `overflow-auto` on an unbounded box never actually
          overflows vertically, so nothing here changes for it. */}
      <div ref={scrollRef} id={scrollId} className="flex-1 min-h-0 overflow-auto">
        {/* ⚠️ min-w-full, NOT w-full, when table-fixed: w-full caps the
            table AT the container's width, so table-layout:fixed has no
            slack to give the last (unsized) column once the other
            columns' widths already add up to more than that — it gets
            squeezed to nothing and visually disappears. min-w-full still
            fills the container when columns are narrow (so the last
            column flexes as intended) but lets the table grow WIDER than
            the container when they don't, which is what actually
            triggers the horizontal scroll. */}
        <table className={columnWidths ? "min-w-full table-fixed text-sm border-collapse" : "w-full text-sm border-collapse"}>
          {columnWidths && (
            <colgroup>
              {columns.map((col) =>
                col.key === lastKey ? (
                  <col key={col.key} />
                ) : (
                  <col key={col.key} style={{ width: columnWidths.getWidth(col.key) }} />
                )
              )}
            </colgroup>
          )}
          {/* z-raised (Estimates.jsx already uses this exact pattern) so the
              sticky header paints over scrolled body rows rather than
              being painted under them by plain DOM order. */}
          <thead className={`${tableHeadRowCls} z-raised`}>
            <tr>
              {columns.map((col) => (
                <SortableColumnHeader
                  key={col.key}
                  col={col}
                  sort={sort}
                  onSort={onSortChange}
                  className={tableHeadCellCls}
                  width={columnWidths?.getWidth(col.key)}
                  resizable={Boolean(columnWidths) && col.key !== lastKey}
                  onResize={columnWidths?.resizeColumn}
                  onResizeEnd={columnWidths?.commitColumnWidth}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={!rowHref && onRowClick ? () => onRowClick(row) : undefined}
                className={`${tableRowHoverCls} ${onRowClick ? "cursor-pointer" : ""}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    // ⚠️⚠️⚠️ EXPLICIT max-width on the <td> ITSELF (not just
                    // min-w-0, not just the <colgroup>'s <col>) — confirmed
                    // by direct DOM-level testing in a live browser, not a
                    // guess: under table-layout:fixed, Chromium still lets a
                    // column's rendered width sit at the LARGER of the
                    // assigned <col> width and the longest content across
                    // every row in that column. `min-width:0` alone does not
                    // override that floor; plain `width` doesn't either;
                    // `max-width` does. min-w-0 stays too — it's harmless and
                    // still correct practice for a cell that needs to shrink.
                    //
                    // ⚠️ `relative` here (not on the <tr>) — a `truncate`
                    // cell has `overflow:hidden`, which clips ANY descendant
                    // trying to visually extend past ITS box, positioned or
                    // not. A single anchor spanning the whole row would get
                    // clipped to just this one cell's width the moment this
                    // cell (or any other) truncates. Bounding the anchor to
                    // its own cell (one per <td>, not one per <tr>) sidesteps
                    // that entirely — nothing ever tries to escape a box that
                    // might clip it.
                    className={`relative min-w-0 ${col.cellClass ?? "px-4 py-2.5 text-ink-muted truncate"}`}
                    style={columnWidths && col.key !== lastKey ? { maxWidth: columnWidths.getWidth(col.key) } : undefined}
                    title={String(col.value?.(row) ?? "") || undefined}
                  >
                    {/* A real <a href> — right-click/middle-click/ctrl-click
                        get the browser's own native handling (see
                        RowLink.jsx); a plain left click still opens the
                        SidePanel via SPA state, same as today. */}
                    {rowHref && onRowClick && (
                      <RowLink to={rowHref(row)} onActivate={() => onRowClick(row)} />
                    )}
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
